"use client";

import { useEffect, useState } from "react";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const testimonials = [{
  author: "Sarah J.",
  quote: "MetalLine Exteriors transformed our home with their standing seam metal roof. The precision and attention to detail were unmatched in Calgary.",
  position: "Homeowner"
}, {
  author: "David M.",
  quote: "The architectural cladding and ACM panels they installed have completely modernized our property. A truly premium experience from start to finish.",
  position: "Property Developer"
}];
export default function Testimonials() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const [active, setActive] = useState(0);
  const goTo = (index: number) => {
    const total = testimonials.length;
    setActive((index % total + total) % total);
  };
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const startInterval = () => {
      if (!intervalId) {
        intervalId = setInterval(() => {
          setActive(prev => (prev + 1) % testimonials.length);
        }, 7000);
      }
    };
    const stopInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        startInterval();
      } else {
        stopInterval();
      }
    };
    handleMediaChange(mediaQuery);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleMediaChange);
    } else {
      mediaQuery.addListener(handleMediaChange);
    }
    return () => {
      stopInterval();
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleMediaChange);
      } else {
        mediaQuery.removeListener(handleMediaChange);
      }
    };
  }, []);
  return <section data-section-id="1969" id="client-testimonials" className="bg-background text-foreground ![padding:30px_24px_65px_24px]">
      <div ref={ref} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <p className="font-default text-sm font-bold uppercase tracking-widest text-primary mb-8">
          Client Success Stories
        </p>

        <h2 className="font-default text-3xl sm:text-4xl font-semibold text-foreground">
          Excellence in Every Detail
        </h2>

        <div className="relative mt-16 min-h-[300px] sm:min-h-[280px]">
          <Quote aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 -translate-y-6 h-32 w-32 sm:h-44 sm:w-44 text-foreground/5" strokeWidth={1} />

          {testimonials.map((item, i) => <div key={i} data-index={i} aria-hidden={active !== i} className={`absolute inset-0 flex flex-col items-center justify-start px-2 transition-opacity duration-1000 ease-in-out ${active === i ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
              <blockquote className="relative mx-auto max-w-xl font-default text-2xl sm:text-3xl font-medium leading-relaxed text-foreground">
                {item.quote}
              </blockquote>

              <div className="mt-10 flex flex-col items-center">
                <Separator className="w-12 bg-primary mb-5" />
                <p className="font-default text-base font-bold text-foreground">
                  {item.author}
                </p>
                <p className="font-default text-sm text-muted-foreground mt-1">
                  {item.position}
                </p>
              </div>
            </div>)}
        </div>

        <div className="mt-12 flex flex-col items-center gap-6">
          <div className="flex items-center justify-center gap-3">
            {testimonials.map((_, i) => <Button key={i} data-index={i} type="button" aria-label={`Go to testimonial ${i + 1}`} onClick={() => goTo(i)} variant="ghost" size="icon" className={`h-2 w-2 p-0 rounded-lg transition-all duration-500 ${active === i ? "w-6 bg-primary" : "bg-primary/30 hover:bg-primary/50"}`} />)}
          </div>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" aria-label="Previous testimonial" onClick={() => goTo(active - 1)} className="h-10 w-10 rounded-lg text-foreground hover:bg-accent hover:text-accent-foreground">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" aria-label="Next testimonial" onClick={() => goTo(active + 1)} className="h-10 w-10 rounded-lg text-foreground hover:bg-accent hover:text-accent-foreground">
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>;
}